/**
 * Runs console for an inbound-file feed (see INBOUND_FILE_RUNS_IMPLEMENTATION_PLAN):
 * the run list on the left, and for the selected run what landed, what didn't,
 * and why.
 *
 * Everything here reads the persisted `import_stats` record. The file is never
 * re-read, so an excluded row is shown as its row number plus the reason — never
 * as the row's own cells.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import Button from '../../components/ui/Button';
import { handleApiError } from '../../services/api';
import { integrationsApi } from '../../services/integrations-api';
import type {
  ImportStats,
  InboundEmailDetail,
  InboundEmailListItem,
  InboundFileVersion,
  RunExclusion,
  RunExclusionsPayload,
} from '../../services/integrations-api';
import { FAMILY_STYLE, slotFamily, slotLabel } from './inboundFileMapping';
import {
  coverageText,
  excludedCount,
  formatRunDay,
  formatWhen,
  matchesFilter,
  rejectReasonText,
  rollUpReasons,
  runOutcomeLine,
  runTitle,
  statusBadgeClass,
  statusDotClass,
  statusText,
  type RunFilter,
} from './runStatus';

const PAGE_SIZE = 25;

type PaneTab = 'summary' | 'excluded' | 'audit';

const RUN_FILTERS: { value: RunFilter; label: string }[] = [
  { value: 'all', label: 'All runs' },
  { value: 'excluded', label: 'Only with exclusions' },
  { value: 'attention', label: 'Only held or failed' },
];

const KIND_LABEL: Record<RunExclusion['kind'], string> = {
  parse: "Couldn't be parsed",
  rule: 'Rejected by a rule',
};

const KIND_TONE: Record<RunExclusion['kind'], { text: string; bg: string; cell: string }> = {
  parse: { text: 'text-alert', bg: 'bg-alert', cell: 'bg-alert/15 text-alert' },
  rule: { text: 'text-warn', bg: 'bg-warn', cell: 'bg-warn/15 text-warn' },
};

const num = (n: number): string => n.toLocaleString();

/**
 * Names the map a run executed with. A run that predates the current approval
 * says so rather than borrowing the live version's number — mapping versions are
 * immutable, so an old run really did use a different one.
 */
function mapLabel(stats: ImportStats, approved: InboundFileVersion | null): React.ReactNode {
  if (approved && approved.id === stats.mappingVersionId) {
    return `Map ${approved.version}${
      approved.approvedAt ? ` · approved ${formatRunDay(approved.approvedAt)}` : ''
    }`;
  }
  return (
    <>
      an earlier map <span className="font-mono text-xs">{stats.mappingVersionId.slice(0, 8)}</span>
    </>
  );
}

// ── Presentational bits ──────────────────────────────────────────────────────

const Tile: React.FC<{
  value: React.ReactNode;
  label: React.ReactNode;
  extra?: React.ReactNode;
  tone?: 'good' | 'warn';
}> = ({ value, label, extra, tone }) => (
  <div className="rounded-lg border border-border bg-card px-3 py-2.5">
    <div
      className={`text-lg font-bold tabular-nums ${
        tone === 'good' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    {extra != null && <div className="mt-1 text-[11px] text-muted-foreground">{extra}</div>}
  </div>
);

const OutcomeCard: React.FC<{
  value: number;
  label: React.ReactNode;
  accent: string;
  valueClass: string;
}> = ({ value, label, accent, valueClass }) => (
  <div className={`rounded-lg border border-border border-l-[3px] px-3.5 py-2.5 ${accent}`}>
    <div className={`text-[22px] font-bold tabular-nums ${valueClass}`}>{num(value)}</div>
    <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
    {children}
  </h3>
);

const DefList: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({ rows }) => (
  <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-[13px]">
    {rows.map(([term, value]) => (
      <React.Fragment key={term}>
        <dt className="whitespace-nowrap text-muted-foreground">{term}</dt>
        <dd className="m-0 break-words text-foreground">{value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

// ── Console ──────────────────────────────────────────────────────────────────

interface Props {
  dataSourceId: string;
  /** Preselected run from `?run=` on the edit page. */
  initialRunId?: string | null;
  onReviewMapping?: () => void;
}

const InboundFileRuns: React.FC<Props> = ({ dataSourceId, initialRunId, onReviewMapping }) => {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<RunFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(initialRunId ?? null);
  const [paneTab, setPaneTab] = useState<PaneTab>('summary');

  // Same query key as InboundEmailActivity's unfiltered page, so the two
  // surfaces share one cache entry.
  const listQuery = useQuery({
    queryKey: ['inbound-emails', dataSourceId, '', offset],
    queryFn: () => integrationsApi.listInboundEmails(dataSourceId, { limit: PAGE_SIZE, offset }),
  });

  // Memoized so an undefined page doesn't hand the aggregates a fresh [] each render.
  const runs = useMemo(() => listQuery.data?.emails ?? [], [listQuery.data]);
  const total = listQuery.data?.total ?? 0;
  const visible = useMemo(() => runs.filter((r) => matchesFilter(r, filter)), [runs, filter]);

  useEffect(() => {
    if (!selectedId && visible.length > 0) setSelectedId(visible[0].id);
  }, [selectedId, visible]);

  const detailQuery = useQuery({
    queryKey: ['inbound-email-detail', dataSourceId, selectedId],
    queryFn: () => integrationsApi.getInboundEmailDetail(dataSourceId, selectedId as string),
    enabled: !!selectedId,
  });

  // Only to name the map a run executed with. Same key as the Review tab, so
  // moving between the two tabs costs no extra request.
  const mapQuery = useQuery({
    queryKey: ['inbound-file-draft', dataSourceId],
    queryFn: () => integrationsApi.getInboundFileDraft(dataSourceId),
    enabled: !!selectedId,
  });
  const approvedMap = mapQuery.data?.approved ?? null;

  const rerun = useMutation({
    mutationFn: (runId: string) => integrationsApi.reprocessInboundEmail(dataSourceId, runId),
    onSuccess: () => {
      toast.success('Queued — the file will be read again shortly');
      void queryClient.invalidateQueries({ queryKey: ['inbound-emails', dataSourceId] });
      void queryClient.invalidateQueries({
        queryKey: ['inbound-email-detail', dataSourceId, selectedId],
      });
    },
    onError: (error) => toast.error(handleApiError(error) || 'Could not queue the file'),
  });

  const fromList = runs.find((r) => r.id === selectedId) ?? null;
  const detail: InboundEmailDetail | null = detailQuery.data ?? null;
  const selected: InboundEmailListItem | null = detail ?? fromList;
  const stats = selected?.importStats ?? null;
  const exclusions = stats?.exclusions ?? [];

  // ── Page-level tiles. These describe the runs actually loaded, and say so —
  // the API has no aggregate endpoint and a "last 90 days" claim would be a lie.
  const summary = useMemo(() => {
    const imported = runs.filter((r) => r.status === 'completed');
    const withStats = imported.map((r) => r.importStats).filter((s): s is ImportStats => !!s);
    const written = withStats.reduce((sum, s) => sum + s.writtenRows, 0);
    const rejected = withStats.reduce((sum, s) => sum + s.rejectedRows, 0);
    const failed = withStats.reduce((sum, s) => sum + s.failedRows, 0);
    const held = runs.filter((r) => r.status === 'quarantined' || r.status === 'awaiting_map').length;
    const notImported = runs.filter(
      (r) => r.status === 'discarded' || r.status === 'failed',
    ).length;
    const days = withStats.map((s) => s.coverage).filter((c): c is NonNullable<typeof c> => !!c);
    const span = days.length
      ? {
          minDate: days.reduce((a, c) => (c.minDate < a ? c.minDate : a), days[0].minDate),
          maxDate: days.reduce((a, c) => (c.maxDate > a ? c.maxDate : a), days[0].maxDate),
        }
      : null;
    return { imported: imported.length, written, rejected, failed, held, notImported, span };
  }, [runs]);

  if (listQuery.isLoading) {
    return <p className="py-8 text-center text-muted-foreground">Loading runs…</p>;
  }

  if (runs.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        No files received yet. Forward a file to this feed's address and its run will appear here.
      </p>
    );
  }

  const excludedTotal = summary.rejected + summary.failed;

  return (
    <div className="pt-4">
      {/* Page summary. Only on the newest page — on an older one "last file
          received" and the totals would describe an arbitrary slice. */}
      {offset === 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile
            value={formatRunDay(runs[0].receivedAt)}
            label="Last file received"
            extra={`${formatWhen(runs[0].receivedAt)} · ${statusText(runs[0].status)}`}
          />
          <Tile
            value={
              <>
                {summary.imported}
                <span className="font-medium text-muted-foreground"> / {runs.length}</span>
              </>
            }
            label={`Files imported, last ${runs.length} run${runs.length === 1 ? '' : 's'}`}
            extra={
              summary.held + summary.notImported > 0
                ? `${summary.held} held · ${summary.notImported} not imported`
                : 'every file imported'
            }
          />
          <Tile
            value={num(summary.written)}
            label="Rows written"
            tone="good"
            extra={summary.span ? coverageText(summary.span) : undefined}
          />
          <Tile
            value={num(excludedTotal)}
            label="Rows excluded"
            tone={excludedTotal > 0 ? 'warn' : undefined}
            extra={`${num(summary.rejected)} rule-rejected · ${num(summary.failed)} unparseable`}
          />
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ── run list ── */}
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="border-b border-border bg-muted/40 p-2">
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as RunFilter);
                setSelectedId(null);
              }}
              className="w-full rounded-md border border-input bg-card px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="Filter runs"
            >
              {RUN_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="max-h-[600px] overflow-y-auto">
            {visible.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                No runs match this filter.
              </p>
            )}
            {visible.map((run) => {
              const runStats = run.importStats;
              return (
                <button
                  key={run.id}
                  type="button"
                  aria-current={run.id === selectedId}
                  onClick={() => {
                    setSelectedId(run.id);
                    setPaneTab('summary');
                  }}
                  className={`block w-full border-b border-border px-3 py-2.5 text-left hover:bg-accent ${
                    run.id === selectedId ? 'bg-accent shadow-[inset_2px_0_0_hsl(var(--primary))]' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDotClass(run.status)}`}
                    />
                    <span className="truncate text-[13px] font-semibold text-foreground">
                      {runTitle(run)}
                    </span>
                    <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                      {formatRunDay(run.receivedAt)}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-xs tabular-nums text-muted-foreground">
                    {runOutcomeLine(run)}
                  </div>
                  {runStats && runStats.totalRows > 0 ? (
                    <div className="mt-1.5 flex h-1 gap-0.5 overflow-hidden rounded-sm">
                      <span className="bg-success" style={{ flex: runStats.writtenRows }} />
                      {runStats.rejectedRows > 0 && (
                        <span className="bg-warn" style={{ flex: runStats.rejectedRows }} />
                      )}
                      {runStats.failedRows > 0 && (
                        <span className="bg-alert" style={{ flex: runStats.failedRows }} />
                      )}
                    </div>
                  ) : (
                    <div className="mt-1.5 h-1 rounded-sm bg-muted" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
            <span>
              {visible.length === runs.length
                ? `${offset + 1}–${offset + runs.length} of ${total} run${total === 1 ? '' : 's'}`
                : `${visible.length} of ${runs.length} shown`}
            </span>
            {total > PAGE_SIZE && (
              <span className="flex gap-1.5">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => {
                    setOffset(Math.max(0, offset - PAGE_SIZE));
                    setSelectedId(null);
                  }}
                >
                  Newer
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => {
                    setOffset(offset + PAGE_SIZE);
                    setSelectedId(null);
                  }}
                >
                  Older
                </Button>
              </span>
            )}
          </div>
        </div>

        {/* ── run detail ── */}
        <div className="overflow-hidden rounded-lg border border-border">
          {!selected && (
            <p className="p-6 text-sm text-muted-foreground">Select a run to see what it did.</p>
          )}

          {selected && (
            <>
              <div className="border-b border-border px-4 py-3.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${statusBadgeClass(
                      selected.status,
                    )}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {statusText(selected.status)}
                  </span>
                  <h2 className="m-0 break-all text-base font-semibold text-foreground">
                    {runTitle(selected)}
                  </h2>
                  {/* Re-run is offered on `failed` alone (§5): nothing was read, so
                      nothing can be orphaned. Every other status shows no action,
                      which makes the server's 409 unreachable rather than unlikely. */}
                  {selected.status === 'failed' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="ml-auto shrink-0"
                      onClick={() => rerun.mutate(selected.id)}
                      isLoading={rerun.isPending}
                    >
                      <RefreshCw className="mr-1 h-4 w-4" />
                      Try this file again
                    </Button>
                  )}
                </div>
                <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Received {formatWhen(selected.receivedAt)}
                  {selected.processedAt ? ` · finished ${formatWhen(selected.processedAt)}` : ''}
                  {stats ? ` (${(stats.durationMs / 1000).toFixed(1)}s)` : ''}
                  {' · from '}
                  <span className="break-all">{selected.sender}</span>
                </div>
              </div>

              <div className="flex gap-1 border-b border-border bg-muted/40 px-4">
                {(['summary', 'excluded', 'audit'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPaneTab(t)}
                    className={`-mb-px border-b-2 px-3 py-2.5 text-[13px] ${
                      paneTab === t
                        ? 'border-primary bg-card font-semibold text-foreground'
                        : 'border-transparent font-medium text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t === 'summary' ? 'Summary' : t === 'excluded' ? 'Excluded rows' : 'Audit'}
                    {t === 'excluded' && excludedCount(stats) > 0 && (
                      <span className="ml-1.5 tabular-nums opacity-65">
                        {num(excludedCount(stats))}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {paneTab === 'summary' && (
                  <RunSummary
                    run={selected}
                    stats={stats}
                    exclusions={exclusions}
                    approvedMap={approvedMap}
                    onReviewMapping={onReviewMapping}
                  />
                )}
                {paneTab === 'excluded' && (
                  <ExcludedRows
                    dataSourceId={dataSourceId}
                    runId={selected.id}
                    stats={stats}
                    exclusions={exclusions}
                  />
                )}
                {paneTab === 'audit' && (
                  <RunAudit
                    run={selected}
                    detail={detail}
                    stats={stats}
                    approvedMap={approvedMap}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Summary ──────────────────────────────────────────────────────────────────

const RunSummary: React.FC<{
  run: InboundEmailListItem;
  stats: ImportStats | null;
  exclusions: RunExclusion[];
  approvedMap: InboundFileVersion | null;
  onReviewMapping?: () => void;
}> = ({ run, stats, exclusions, approvedMap, onReviewMapping }) => {
  const reasons = useMemo(() => rollUpReasons(exclusions), [exclusions]);
  const topCount = reasons[0]?.count ?? 0;

  if (!stats) {
    const why = rejectReasonText(run.rejectReason);
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
          <p className="m-0">{runOutcomeLine(run)}</p>
          {why && run.status !== 'quarantined' && run.status !== 'discarded' && (
            <p className="m-0 mt-1 font-mono text-xs text-muted-foreground">{why}</p>
          )}
          <p className="m-0 mt-2 text-xs text-muted-foreground">
            No rows were read, so there is no run record for this file.
          </p>
        </div>
        {run.errorMessage && (
          <div className="rounded-md border border-alert/25 bg-alert/10 p-3 text-sm text-alert">
            {run.errorMessage}
          </div>
        )}
        {run.status === 'quarantined' && onReviewMapping && (
          <div className="rounded-md border border-warn/30 bg-warn/10 p-3 text-[12.5px] text-foreground">
            <p className="m-0 mb-2">
              The file's format no longer matches the approved map, so it was held rather than
              guessed at. Approving an updated mapping brings it in.
            </p>
            <Button variant="secondary" size="sm" onClick={onReviewMapping}>
              Review mapping
            </Button>
          </div>
        )}
      </div>
    );
  }

  const newRows = stats.newRows;
  const updatedRows = stats.updatedRows;

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <OutcomeCard
          value={stats.writtenRows}
          accent="border-l-success"
          valueClass="text-success"
          label={
            <>
              written of {num(stats.totalRows)} read
              {newRows != null && updatedRows != null && (
                <span className="text-muted-foreground">
                  {' '}
                  · {num(newRows)} new, {num(updatedRows)} updated
                </span>
              )}
            </>
          }
        />
        <OutcomeCard
          value={stats.rejectedRows}
          accent="border-l-warn"
          valueClass="text-warn"
          label="rejected by a rule you set"
        />
        <OutcomeCard
          value={stats.failedRows}
          accent="border-l-alert"
          valueClass="text-alert"
          label="couldn't be parsed"
        />
      </div>

      {reasons.length > 0 && (
        <div className="mt-6">
          <SectionTitle>Why rows were excluded</SectionTitle>
          <div className="flex flex-col gap-1.5">
            {reasons.map((r) => (
              <div
                key={`${r.kind}-${r.reason}`}
                className="flex items-center gap-2.5 rounded-md border border-border px-3 py-1.5 text-[13px]"
              >
                <span className={`h-2 w-2 shrink-0 rounded-sm ${KIND_TONE[r.kind].bg}`} />
                <span className="min-w-0 text-foreground">{r.reason}</span>
                <span className="ml-auto hidden h-1.5 w-24 shrink-0 overflow-hidden rounded-sm bg-muted sm:block">
                  <span
                    className={`block h-full ${KIND_TONE[r.kind].bg}`}
                    style={{ width: `${topCount ? (r.count / topCount) * 100 : 0}%` }}
                  />
                </span>
                <span className="w-8 shrink-0 text-right font-semibold tabular-nums text-foreground">
                  {num(r.count)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <SectionTitle>What landed</SectionTitle>
        <DefList
          rows={[
            ['Records', `${num(stats.writtenRows)} row${stats.writtenRows === 1 ? '' : 's'} written`],
            ['Covering', stats.coverage ? coverageText(stats.coverage) : '—'],
            [
              'Duplicate keys',
              stats.duplicateKeys == null
                ? '—'
                : `${num(stats.duplicateKeys)} row${
                    stats.duplicateKeys === 1 ? '' : 's'
                  } shared a natural key inside this file`,
            ],
            ['Written with', mapLabel(stats, approvedMap)],
          ]}
        />
      </div>
    </div>
  );
};

// ── Excluded rows ────────────────────────────────────────────────────────────

/** Row + reason, from the run record. Always available, and the fallback for the grid. */
const StoredExclusionList: React.FC<{ stats: ImportStats; exclusions: RunExclusion[] }> = ({
  stats,
  exclusions,
}) => {
  const byKind: Array<[RunExclusion['kind'], RunExclusion[]]> = (['parse', 'rule'] as const)
    .map(
      (kind) =>
        [kind, exclusions.filter((e) => e.kind === kind)] as [
          RunExclusion['kind'],
          RunExclusion[],
        ],
    )
    .filter(([, rows]) => rows.length > 0);

  return (
    <div className="divide-y divide-border overflow-hidden rounded-md border border-border">
      {byKind.map(([kind, rows]) => (
        <div key={kind}>
          <div className="bg-muted/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
            {KIND_LABEL[kind]} · {num(rows.length)} row{rows.length === 1 ? '' : 's'}
            {kind === 'rule' && stats.rejectedRows > rows.length && (
              <> of {num(stats.rejectedRows)}</>
            )}
            {kind === 'parse' && stats.failedRows > rows.length && (
              <> of {num(stats.failedRows)}</>
            )}
          </div>
          <ul className="divide-y divide-border">
            {rows.map((e) => (
              <li key={`${e.kind}-${e.row}`} className="flex gap-3 px-3 py-1.5 text-[12.5px]">
                <span
                  className={`w-14 shrink-0 font-mono text-[11.5px] tabular-nums ${
                    KIND_TONE[e.kind].text
                  }`}
                >
                  row {e.row}
                </span>
                <span className="min-w-0 text-foreground">{e.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

/**
 * The excluded rows as they appear in the file. Column headers wear the
 * field-map family colours so this grid and the Review mapping screen agree
 * about what a column is, and the reason is pinned right — it is the point of
 * the grid and must never scroll away.
 */
const RawCellGrid: React.FC<{ payload: RunExclusionsPayload }> = ({ payload }) => {
  const { columns, rows } = payload;
  const groups = (['parse', 'rule'] as const)
    .map((kind) => ({ kind, rows: rows.filter((r) => r.kind === kind) }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="w-full min-w-[920px] border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky top-0 z-[1] w-14 border-b border-border bg-card px-2 py-1.5 text-left text-[11px] font-semibold text-muted-foreground">
              Row
            </th>
            {columns.map((col, i) => {
              const family = slotFamily(col.slot);
              return (
                <th
                  key={`${col.header}-${i}`}
                  className={`sticky top-0 z-[1] whitespace-nowrap border-b border-border bg-card px-2 py-1.5 text-left text-[11px] font-semibold ${
                    col.slot ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {col.label ?? col.header}
                  <span
                    className={`mt-0.5 block w-fit rounded-sm px-1 font-mono text-[9.5px] font-semibold ${
                      FAMILY_STYLE[family].badge
                    }`}
                  >
                    {col.masterMetric ? '★ ' : ''}
                    {col.slot ? slotLabel(col.slot) : 'skipped'}
                  </span>
                </th>
              );
            })}
            <th className="sticky right-0 top-0 z-[2] w-[250px] min-w-[250px] border-b border-l border-border bg-card px-2 py-1.5 text-left text-[11px] font-semibold text-muted-foreground">
              Why it was excluded
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.kind}>
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="bg-muted/40 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground"
                >
                  {KIND_LABEL[group.kind]} · {num(group.rows.length)} row
                  {group.rows.length === 1 ? '' : 's'}
                </td>
                {/* Opaque: a translucent sticky cell lets the scrolling cells show through. */}
                <td className="sticky right-0 w-[250px] min-w-[250px] border-l border-border bg-card" />
              </tr>
              {group.rows.map((r) => (
                <tr key={`${r.kind}-${r.row}`}>
                  <td className="border-b border-border px-2 py-1.5 font-mono text-[11.5px] tabular-nums text-muted-foreground">
                    {r.row}
                  </td>
                  {columns.map((col, i) => (
                    <td
                      key={`${r.row}-${i}`}
                      className={`whitespace-nowrap border-b border-border px-2 py-1.5 font-mono text-[11.5px] ${
                        i === r.culpritColumn
                          ? `font-semibold ${KIND_TONE[r.kind].cell}`
                          : `text-muted-foreground ${FAMILY_STYLE[slotFamily(col.slot)].column}`
                      }`}
                    >
                      {r.cells[i] ? r.cells[i] : '—'}
                    </td>
                  ))}
                  <td className="sticky right-0 w-[250px] min-w-[250px] border-b border-l border-border bg-card px-2 py-1.5 text-xs text-foreground">
                    {r.reason}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ExcludedRows: React.FC<{
  dataSourceId: string;
  runId: string;
  stats: ImportStats | null;
  exclusions: RunExclusion[];
}> = ({ dataSourceId, runId, stats, exclusions }) => {
  const total = excludedCount(stats);

  // The file is re-read to answer this, and the answer is deterministic — so it
  // is worth caching for the session rather than re-fetching on every glance.
  const gridQuery = useQuery({
    queryKey: ['inbound-run-exclusions', dataSourceId, runId],
    queryFn: () => integrationsApi.listRunExclusions(dataSourceId, runId),
    enabled: total > 0,
    staleTime: 5 * 60_000,
  });

  if (!stats || total === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {stats ? 'Every row in this file was written.' : 'This file was never read.'}
      </p>
    );
  }

  const grid = gridQuery.data;
  const hasGrid = grid?.available === true && grid.rows.length > 0;

  return (
    <div>
      {hasGrid ? (
        <RawCellGrid payload={grid} />
      ) : exclusions.length > 0 ? (
        <StoredExclusionList stats={stats} exclusions={exclusions} />
      ) : (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
          {num(total)} row{total === 1 ? '' : 's'} were excluded, but this run was recorded before
          per-row reasons were kept. The counts on Summary are exact.
        </div>
      )}

      {grid?.available === false && grid.unavailableReason && (
        <div className="mt-2 rounded-md border border-info/25 bg-info/10 p-3 text-[12.5px] text-foreground">
          {grid.unavailableReason}
        </div>
      )}

      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
        {gridQuery.isFetching && 'Reading the file for the original rows… '}
        {(grid?.truncated || stats.exclusionOverflow) &&
          `Detail is kept for the first ${num(
            Math.max(exclusions.length, grid?.rows.length ?? 0),
          )} excluded rows per file — the counts above are always exact. `}
        Row numbers and reasons are stored with the run; the file's own cells are not — they are
        read from the original file each time you open this tab.
      </p>
    </div>
  );
};

// ── Audit ────────────────────────────────────────────────────────────────────

const RunAudit: React.FC<{
  run: InboundEmailListItem;
  detail: InboundEmailDetail | null;
  stats: ImportStats | null;
  approvedMap: InboundFileVersion | null;
}> = ({ run, detail, stats, approvedMap }) => {
  const attachment = detail?.attachments[0] ?? null;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <SectionTitle>Delivery</SectionTitle>
        <DefList
          rows={[
            ['From', <span className="break-all">{run.sender}</span>],
            [
              'To',
              detail ? (
                <span className="break-all font-mono text-xs">{detail.recipient}</span>
              ) : (
                '—'
              ),
            ],
            ['Subject', run.subject || '(no subject)'],
            [
              'Attachment',
              attachment
                ? `${attachment.name}${
                    attachment.size ? ` · ${Math.round(attachment.size / 1024)} KB` : ''
                  }${attachment.contentType ? ` · ${attachment.contentType}` : ''}`
                : `${run.attachmentCount} attachment${run.attachmentCount === 1 ? '' : 's'}`,
            ],
          ]}
        />
      </div>
      <div>
        <SectionTitle>Processing</SectionTitle>
        <DefList
          rows={[
            ['Received', formatWhen(run.receivedAt)],
            ['Finished', formatWhen(run.processedAt)],
            [
              'Executed with',
              stats ? (
                <>
                  {mapLabel(stats, approvedMap)}{' '}
                  <span className="font-mono text-xs text-muted-foreground">
                    {stats.mappingVersionId}
                  </span>
                </>
              ) : (
                '—'
              ),
            ],
            [
              'Reason',
              rejectReasonText(run.rejectReason) ? (
                <span className="font-mono text-xs">{rejectReasonText(run.rejectReason)}</span>
              ) : (
                '—'
              ),
            ],
            [
              'Error',
              run.errorMessage ? (
                <span className="text-alert">{run.errorMessage}</span>
              ) : (
                '—'
              ),
            ],
            [
              'AI session',
              detail?.llmSessionId ? (
                <span className="font-mono text-xs">{detail.llmSessionId}</span>
              ) : (
                'none — this run executed the approved map'
              ),
            ],
          ]}
        />
      </div>
    </div>
  );
};

export default InboundFileRuns;
